import React from 'react';
import { TruckIcon, MapPinIcon } from '@heroicons/react/24/outline';

const ShipmentProgressBar = ({ currentStatus, senderLocation, recipientLocation, progressPercentage }) => {
  // Use admin-set percentage, fallback to status-based calculation if not provided
  const getProgressPercentage = (status, adminPercentage) => {
    if (adminPercentage !== undefined && adminPercentage !== null) {
      return adminPercentage;
    }
    
    const statusProgress = {
      pending: 0,
      picked_up: 15,
      in_transit: 50,
      out_for_delivery: 90,
      delivered: 100,
      exception: 30,
      on_hold: 30,
      cancelled: 0,
      returned: 0
    };
    return statusProgress[status] || 0;
  };

  const progress = getProgressPercentage(currentStatus, progressPercentage);
  
  // Brand palette: blue progress, green only when delivered
  const getProgressColor = (progress) => {
    if (progress === 100) return 'bg-[#16A34A]';
    if (progress > 0) return 'bg-[#FF6B00]';
    return 'bg-[#E2E8F0]';
  };

  const progressColor = getProgressColor(progress);

  return (
    <div className="w-full bg-white rounded-xl p-4 sm:p-6 md:p-8 mb-6 shadow-lg border-2 border-[#E2E8F0]">
      {/* Source and Destination Headers */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="bg-orange-100 p-1.5 sm:p-2 rounded-lg">
            <MapPinIcon className="h-5 w-5 sm:h-7 sm:w-7 text-[#FF6B00]" />
          </div>
          <div>
            <p className="text-[10px] sm:text-xs text-[#64748B] uppercase tracking-wide font-medium">Source</p>
            <span className="text-sm sm:text-base font-bold text-[#0B1F3A]">From</span>
          </div>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-3">
          <div>
            <p className="text-[10px] sm:text-xs text-[#64748B] uppercase tracking-wide font-medium text-right">Destination</p>
            <span className="text-sm sm:text-base font-bold text-[#0B1F3A]">To</span>
          </div>
          <div className="bg-red-100 p-1.5 sm:p-2 rounded-lg">
            <MapPinIcon className="h-5 w-5 sm:h-7 sm:w-7 text-[#DC2626]" />
          </div>
        </div>
      </div>

      {/* Progress Bar with Cargo Icon */}
      <div className="relative mb-6 sm:mb-8 px-1 sm:px-4 md:px-6">
        <div className="h-5 sm:h-6 bg-[#E2E8F0] rounded-full overflow-visible relative">
          <div 
            className={`h-full ${progressColor} transition-all duration-700 ease-out rounded-full`}
            style={{ width: `${progress}%` }}
          >
          </div>
          {/* Cargo truck icon that moves with progress */}
          <div 
            className="absolute top-1/2 flex items-center justify-center transition-all duration-700 ease-out z-10"
            style={{ left: `${Math.max(progress, 2)}%`, transform: 'translate(-50%, -50%)' }}
          >
            <div className="bg-white border-2 border-[#FF6B00] rounded-full p-1 sm:p-2 shadow-lg">
              <TruckIcon className="h-5 w-5 sm:h-7 sm:w-7 md:h-8 md:w-8 text-[#FF6B00]" />
            </div>
          </div>
        </div>
      </div>

      {/* Percentage and Location Info */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between mb-6 gap-3 sm:gap-4">
        <div className="text-left bg-[#F5F7FA] rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 flex-1">
          <p className="text-[10px] sm:text-xs text-[#64748B] uppercase tracking-wide mb-1">From</p>
          <p className="text-xs sm:text-sm md:text-base font-bold text-[#17202A] break-words">
            {senderLocation}
          </p>
        </div>

        <div className="text-center flex-shrink-0 self-center">
          <div className="inline-flex flex-col items-center bg-orange-50 px-3 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-3 rounded-xl shadow-sm border-2 border-orange-200">
            <span className="text-xl sm:text-2xl md:text-3xl font-extrabold text-[#FF6B00]">
              {progress}%
            </span>
            <span className="text-[10px] sm:text-xs text-[#FF6B00] font-medium uppercase tracking-wide">complete</span>
          </div>
        </div>

        <div className="text-right bg-[#F5F7FA] rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 flex-1">
          <p className="text-[10px] sm:text-xs text-[#64748B] uppercase tracking-wide mb-1">To</p>
          <p className="text-xs sm:text-sm md:text-base font-bold text-[#17202A] break-words">
            {recipientLocation}
          </p>
        </div>
      </div>

      {/* Status Milestones */}
      <div className="mt-4 sm:mt-6 grid grid-cols-5 gap-0.5 sm:gap-1 md:gap-2">
        {[
          { label: 'Pending', shortLabel: 'Pending', value: 0, status: 'pending', order: 0 },
          { label: 'Picked Up', shortLabel: 'Picked', value: 15, status: 'picked_up', order: 1 },
          { label: 'In Transit', shortLabel: 'Transit', value: 30, status: 'in_transit', order: 2 },
          { label: 'Out for Delivery', shortLabel: 'Delivery', value: 90, status: 'out_for_delivery', order: 3 },
          { label: 'Delivered', shortLabel: 'Delivered', value: 100, status: 'delivered', order: 4 }
        ].map((milestone) => {
          const currentOrder = [
            'pending', 'picked_up', 'in_transit', 'out_for_delivery', 'delivered'
          ].indexOf(currentStatus);
          const isCurrentStatus = currentStatus === milestone.status;
          const isCompleted = milestone.order < currentOrder;
          
          return (
            <div 
              key={milestone.label}
              className={`text-center ${
                isCurrentStatus 
                  ? 'text-[#FF6B00] font-bold' 
                  : isCompleted 
                    ? 'text-[#0B1F3A] font-semibold' 
                    : 'text-[#64748B]'
              }`}>
              <div className={`h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 rounded-full mx-auto mb-1.5 sm:mb-2 ${
                isCurrentStatus 
                  ? 'bg-[#FF6B00] ring-2 sm:ring-4 ring-orange-200' 
                  : isCompleted 
                    ? 'bg-[#0B1F3A]' 
                    : 'bg-[#E2E8F0]'
              }`} />
              <span className="text-[9px] sm:text-xs md:text-sm leading-tight block">{milestone.shortLabel}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShipmentProgressBar;
